import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Gauge,
  Inbox,
  Search,
  ShieldCheck,
  Server,
  ShieldOff,
  Bot,
  CheckCheck,
  Boxes,
  SlidersHorizontal,
  Settings,
  Plug,
  type LucideIcon,
} from "lucide-react";
import { useApprovalsSummary } from "@/hooks/security/useApprovals";

type Item = { title: string; url: string; icon: LucideIcon; badgeKey?: "pendingApprovals" };

const security: Item[] = [
  { title: "Posture", url: "/posture", icon: Gauge },
  { title: "Triage", url: "/triage", icon: Inbox },
  { title: "Investigate", url: "/investigate", icon: Search },
  { title: "Coverage", url: "/coverage", icon: ShieldCheck },
  { title: "Fleet", url: "/fleet", icon: Server },
  { title: "IOC Blocklist", url: "/security/iocs", icon: ShieldOff },
];

const lighthouse: Item[] = [
  { title: "Posture", url: "/ai/posture", icon: Bot },
  { title: "Approvals", url: "/ai/approvals", icon: CheckCheck, badgeKey: "pendingApprovals" },
  { title: "Inventory", url: "/ai/inventory", icon: Boxes },
  { title: "Controls", url: "/ai/controls", icon: SlidersHorizontal },
];

const system: Item[] = [
  { title: "MCP", url: "/system/mcp", icon: Plug },
  { title: "Settings", url: "/settings", icon: Settings },
];

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-4 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
      {children}
    </div>
  );
}

function Section({ label, items, badges }: { label: string; items: Item[]; badges?: Record<string, number> }) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { pathname } = useLocation();
  return (
    <SidebarGroup>
      {!collapsed && <SectionLabel>{label}</SectionLabel>}
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const active =
              pathname === item.url || pathname.startsWith(item.url + "/");
            const badgeCount = item.badgeKey ? badges?.[item.badgeKey] ?? 0 : 0;
            return (
              <SidebarMenuItem key={item.url}>
                <SidebarMenuButton asChild isActive={active} tooltip={item.title}>
                  <NavLink to={item.url} className="flex items-center gap-2.5">
                    <item.icon className="h-4 w-4 flex-none" />
                    {!collapsed && (
                      <>
                        <span className="text-[13px] flex-1">{item.title}</span>
                        {badgeCount > 0 && (
                          <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-warning-muted text-warning text-[10px] font-semibold px-1.5 tabular-nums">
                            {badgeCount > 99 ? "99+" : badgeCount}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

export function AppSidebar() {
  const summary = useApprovalsSummary();
  const badges = { pendingApprovals: summary.data?.pending ?? 0 };
  return (
    <Sidebar collapsible="icon">
      <SidebarContent className="pt-16">
        <Section label="Security" items={security} />
        <Section label="AI Governance" items={lighthouse} badges={badges} />
        <Section label="System" items={system} />
      </SidebarContent>
    </Sidebar>
  );
}

export default AppSidebar;
